import {
  X,
  BookOpen,
  Calendar,
  ArrowDownCircle,
  ArrowUpCircle,
  Scale,
  User,
  Tag,
  Clock,
  FileText,
} from 'lucide-react';
import { Button } from '../../components/ui/button';

interface CashbookEntryDetailsModalProps {
  open: boolean;
  onClose: () => void;
  entry: any | null;
}

const CURRENCY = '৳';

const TYPE_LABELS: Record<string, string> = {
  opening: 'Opening Balance',
  closing: 'Closing Balance',
  adjustment: 'Cash Adjustment',
  receipt: 'Receipt',
  refund: 'Refund',
  expense: 'Expense',
  payment: 'Payment',
};

/**
 * Friendly names for the internal `referenceType` keys so users never see
 * raw snake_case/camelCase identifiers in the details view.
 */
const REFERENCE_TYPE_LABELS: Record<string, string> = {
  payment: 'Patient Payment',
  refund: 'Patient Refund',
  expense: 'Cash Expense',
  fixed_asset: 'Fixed Asset Purchase',
  asset_invoice: 'Asset Invoice',
  appointment: 'Appointment',
  charge_event: 'Charge Event',
  doctor_payment: 'Doctor Payment',
  DiagnosticReceipt: 'Diagnostic Receipt',
  PHARMACY_SALE: 'Pharmacy Sale',
  PHARMACY_SALE_RETURN: 'Pharmacy Sale Return',
  OPENING_STOCK: 'Opening Stock',
  GOODS_RECEIPT: 'Goods Receipt',
  PURCHASE_RETURN: 'Purchase Return',
  GRN_DELETE: 'Goods Receipt Reversal',
  EXPIRY_WRITE_OFF: 'Expiry Write-off',
  PRESCRIPTION: 'Prescription',
  ISSUE: 'Stock Issue',
  RETURN: 'Stock Return',
  MANUAL: 'Manual Adjustment',
};

function referenceTypeLabel(type?: string | null): string {
  if (!type) return '—';
  const key = type.trim();
  const known = REFERENCE_TYPE_LABELS[key] || REFERENCE_TYPE_LABELS[key.toUpperCase()];
  if (known) return known;
  // Fallback: turn an unknown internal key into a readable label.
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function fmtMoney(value: number | undefined | null): string {
  return `${CURRENCY}${(Number(value) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function fmtDateTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function enteredByName(entry: any): string {
  const user = entry?.enteredBy;
  if (!user) return '—';
  return user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || '—';
}

function DetailItem({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-xs font-medium text-text-muted uppercase mb-1 flex items-center gap-1.5">
        <Icon size={12} />
        {label}
      </p>
      <p className="text-sm text-text break-words">{value}</p>
    </div>
  );
}

export function CashbookEntryDetailsModal({ open, onClose, entry }: CashbookEntryDetailsModalProps) {
  if (!open || !entry) return null;

  const debit = Number(entry.debit) || 0;
  const credit = Number(entry.credit) || 0;
  const isCashIn = debit > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-border bg-background/50 rounded-t-2xl">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <BookOpen size={20} className="text-primary" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-text truncate">
                {TYPE_LABELS[entry.type] || entry.type}
              </h2>
              <p className="text-xs text-text-muted">
                {entry.date ? new Date(entry.date).toLocaleDateString() : '—'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-background rounded-xl transition-colors"
          >
            <X size={18} className="text-text-muted" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="rounded-xl border border-border bg-background/50 p-4">
            <p className="text-xs font-medium text-text-muted uppercase">Entry Description</p>
            <p className="text-sm text-text mt-1 whitespace-pre-wrap">{entry.entryDescription || '—'}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg border border-border bg-background/50 p-3">
              <p className="text-xs font-medium text-text-muted uppercase flex items-center gap-1.5">
                <ArrowDownCircle size={12} />
                Debit (Cash In)
              </p>
              <p className="text-lg font-bold text-success mt-1">{fmtMoney(debit)}</p>
            </div>
            <div className="rounded-lg border border-border bg-background/50 p-3">
              <p className="text-xs font-medium text-text-muted uppercase flex items-center gap-1.5">
                <ArrowUpCircle size={12} />
                Credit (Cash Out)
              </p>
              <p className="text-lg font-bold text-danger mt-1">{fmtMoney(credit)}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DetailItem
              icon={Scale}
              label="Running Balance"
              value={<span className={isCashIn ? 'text-success' : 'text-text'}>{fmtMoney(entry.runningBalance)}</span>}
            />
            <DetailItem icon={Tag} label="Type" value={TYPE_LABELS[entry.type] || entry.type} />
            <DetailItem icon={Clock} label="Shift" value={entry.shiftCode || '—'} />
            <DetailItem icon={User} label="Entered By" value={enteredByName(entry)} />
            <DetailItem icon={FileText} label="Reference" value={referenceTypeLabel(entry.referenceType)} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-border">
            <DetailItem icon={Calendar} label="Created" value={fmtDateTime(entry.createdAt)} />
            <DetailItem icon={Calendar} label="Last updated" value={fmtDateTime(entry.updatedAt)} />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-border">
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
